import {
  ButtonSettings,
  PageButtonSettings,
  TrackingPageSettings,
} from "@redotech/order-tracking-common/tracking-page-definition";
import { cssFontFamily } from "@redotech/redo-content-builder-common/brand-kit/font-utils";
import { ButtonHierarchy } from "@redotech/redo-content-builder-common/element-schema";

const fontWeightMap: Record<string, number> = {
  thin: 300,
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

/**
 * Common block styling utilities for tracking page blocks
 */
export function getBlockStyles(pageSettings: TrackingPageSettings | undefined) {
  return {
    backgroundColor: pageSettings?.blockSettings.backgroundColor,
    borderRadius: pageSettings?.blockSettings.borderRadius,
    borderColor: pageSettings?.blockSettings.borderColor,
    fontFamily: pageSettings?.typography.bodyFontFamily,
    color: pageSettings?.typography.bodyFontColor,
  };
}

const HEADING_TAGS = ["h1", "h2", "h3", "h4", "h5", "h6"] as const;

/** Browser defaults Tailwind's preflight strips; `em` keeps them proportional. */
const HEADING_SCALE: Record<(typeof HEADING_TAGS)[number], string> = {
  h1: "2em",
  h2: "1.5em",
  h3: "1.17em",
  h4: "1em",
  h5: "0.83em",
  h6: "0.67em",
};

/** Both surfaces share heading rules; only list markers differ. */
export type TextBlockSurface = "page" | "editor";

/** A block-level family replaces the page's for headings as well as body. */
export function resolveTextBlockBodyFont(
  pageSettings: TrackingPageSettings | undefined,
  blockFontFamily: string | undefined,
): string | undefined {
  return blockFontFamily ?? pageSettings?.typography.bodyFontFamily;
}

export function textBlockContentStyles({
  containerId,
  pageSettings,
  linkColor,
  surface = "page",
  blockFontFamily,
}: {
  containerId: string;
  pageSettings: TrackingPageSettings | undefined;
  linkColor: string;
  surface?: TextBlockSurface;
  blockFontFamily?: string;
}): string {
  const sel = (tag: string) => `#${containerId} ${tag}`;
  const headings = HEADING_TAGS.map(sel).join(", ");
  const headerFontFamily =
    blockFontFamily ?? pageSettings?.typography.headerFontFamily;

  return `
${headings}, ${sel("p")} { margin: 0; padding: 0; }
${HEADING_TAGS.map(
  (tag) =>
    `${sel(tag)} { font-size: ${HEADING_SCALE[tag]}; font-weight: 700; }`,
).join("\n")}
${headerFontFamily ? `${headings} { font-family: ${cssFontFamily(headerFontFamily)}; }` : ""}
${sel("a")} { color: ${linkColor}; text-decoration: underline; }
${
  surface === "page"
    ? // 3em per nested level matches the editor's `ql-indent-N` steps.
      `${sel("ul")}, ${sel("ol")} { padding-left: 1.5em; }
${sel("li")} > ul, ${sel("li")} > ol { padding-left: 3em; }
${sel("li")} { display: list-item; }
${sel("ul")} > li { list-style-type: disc; }
${sel("ol")} > li { list-style-type: decimal; }`
    : // Zero the list, not the item: item padding is where `ql-indent-N` nests.
      // Quill's absolute `.ql-ui` dot strands when a line grows; replace it.
      `${sel("ul")}, ${sel("ol")} { padding-left: 0; }
${sel('li[data-list="bullet"]')} > .ql-ui { display: none; }
${sel('li[data-list="bullet"]')}::before { content: "\\2022"; display: inline-block; width: 1.2em; margin-left: -1.5em; margin-right: 0.3em; text-align: right; }`
}
`;
}

export function getHeaderStyles(
  pageSettings: TrackingPageSettings | undefined,
) {
  return {
    fontFamily: pageSettings?.typography.headerFontFamily,
    color: pageSettings?.typography.headerFontColor,
    ...(pageSettings?.typography.headerFontSize
      ? { fontSize: `${pageSettings.typography.headerFontSize}px` }
      : {}),
    ...(pageSettings?.typography.headerFontWeight
      ? { fontWeight: fontWeightMap[pageSettings.typography.headerFontWeight] }
      : {}),
  };
}

export function getBadgeStyles(pageSettings: TrackingPageSettings | undefined) {
  const badge = pageSettings?.badgeSettings;
  if (!badge) {
    return undefined;
  }
  return {
    backgroundColor: badge.backgroundColor,
    borderColor: badge.borderColor,
    borderRadius: badge.borderRadius,
    // Feed the merchant fontColor through a custom property so the CSS rule can
    // apply it with !important (to win the customer-page cascade override)
    // while still honoring merchant config; falls back to the theme on-brand
    // color when unset.
    ["--quantity-badge-color"]: badge.fontColor,
  };
}

export function getBodyStyles(pageSettings: TrackingPageSettings | undefined) {
  return {
    fontFamily: pageSettings?.typography.bodyFontFamily,
    color: pageSettings?.typography.bodyFontColor,
    ...(pageSettings?.typography.bodyFontSize
      ? { fontSize: `${pageSettings.typography.bodyFontSize}px` }
      : {}),
    ...(pageSettings?.typography.bodyFontWeight
      ? { fontWeight: fontWeightMap[pageSettings.typography.bodyFontWeight] }
      : {}),
  };
}

// label styles are just 60% opacity of body styles
export function getLabelStyles(pageSettings: TrackingPageSettings | undefined) {
  return {
    fontFamily: pageSettings?.typography.bodyFontFamily,
    color: pageSettings?.typography.bodyFontColor
      ? `${pageSettings.typography.bodyFontColor}99`
      : undefined,
    ...(pageSettings?.typography.bodyFontSize
      ? { fontSize: `${pageSettings.typography.bodyFontSize}px` }
      : {}),
    ...(pageSettings?.typography.bodyFontWeight
      ? { fontWeight: fontWeightMap[pageSettings.typography.bodyFontWeight] }
      : {}),
  };
}

export function getDeliveryDateStyles(
  pageSettings: TrackingPageSettings | undefined,
) {
  return pageSettings?.typography.deliveryDateFontSize
    ? {
        fontSize: `${pageSettings.typography.deliveryDateFontSize}px`,
        lineHeight: `${pageSettings.typography.deliveryDateFontSize}px`,
      }
    : {};
}

export function getButtonBorderRadius(
  pageSettings: TrackingPageSettings | undefined,
): Record<string, string> | undefined {
  if (pageSettings?.buttonBorderRadius === undefined) {
    return undefined;
  }
  return { borderRadius: `${pageSettings.buttonBorderRadius}px` };
}

export function getScrollbarHiddenStyles() {
  return {
    overflowY: "auto" as const,
    overflowX: "hidden" as const,
    scrollbarWidth: "none" as const,
    msOverflowStyle: "none" as const,
  };
}

export function getLinkStyles(pageSettings: TrackingPageSettings | undefined) {
  return { color: pageSettings?.typography.linkFontColor };
}

export function getTrackingNumberUrlStyles(
  pageSettings: TrackingPageSettings | undefined,
) {
  return {
    color: pageSettings?.typography.linkFontColor,
    ...(pageSettings?.typography.trackingNumberUrlFontSize
      ? { fontSize: `${pageSettings.typography.trackingNumberUrlFontSize}px` }
      : {}),
  };
}

/**
 * Get button styles for a specific hierarchy, prioritizing block-level settings
 *
 * @param hierarchy - The button hierarchy (primary, secondary, or tertiary)
 * @param buttonSettings - Block-level button settings
 * @returns Style object with backgroundColor, color, outlineColor, and CSS variables for hover/active states
 */
export function getButtonStyles(
  pageSettings: PageButtonSettings | undefined,
  buttonSettings: ButtonSettings | undefined,
) {
  if (buttonSettings?.hierarchy === "custom") {
    return {
      backgroundColor:
        buttonSettings?.buttonConfiguration.default.style.background,
      color: buttonSettings?.buttonConfiguration.default.text.fontColor,
      outlineColor: buttonSettings?.buttonConfiguration.default.style.border,
      ["--button-bg-hover"]:
        buttonSettings?.buttonConfiguration.hovered?.style.background,
      ["--button-color-hover"]:
        buttonSettings?.buttonConfiguration.hovered?.text.fontColor,
      ["--button-outline-hover"]:
        buttonSettings?.buttonConfiguration.hovered?.style.border,
      ["--button-bg-active"]:
        buttonSettings?.buttonConfiguration.active?.style.background,
      ["--button-color-active"]:
        buttonSettings?.buttonConfiguration.active?.text.fontColor,
      ["--button-outline-active"]:
        buttonSettings?.buttonConfiguration.active?.style.border,
    };
  }
  const settings = pageSettings?.[buttonSettings?.hierarchy ?? "primary"];
  return {
    backgroundColor: settings?.default.style.background,
    color: settings?.default.text.fontColor,
    outlineColor: settings?.default.style.border,
    ["--button-bg-hover"]: settings?.hovered?.style.background,
    ["--button-color-hover"]: settings?.hovered?.text.fontColor,
    ["--button-outline-hover"]: settings?.hovered?.style.border,
    ["--button-bg-active"]: settings?.active?.style.background,
    ["--button-color-active"]: settings?.active?.text.fontColor,
    ["--button-outline-active"]: settings?.active?.style.border,
  };
}

export function getButtonHierarchy(
  buttonSettings: ButtonSettings | undefined,
): ButtonHierarchy {
  if (!buttonSettings?.hierarchy) {
    return "primary";
  }
  return buttonSettings?.hierarchy !== "custom"
    ? buttonSettings?.hierarchy
    : "primary";
}
